"use client";

// Visual building blocks for the public course pages. Fed by
// lib/courses/parse-course-md.ts so every course's own copy drives the
// pictures: no per-course hand-written content here.

import type { CSSProperties, ReactNode } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Bug, Compass, Cpu, EyeOff, FileText, Globe, KeyRound, Languages, ListChecks, Lock,
  MonitorSmartphone, Network, Radar, ScanSearch, ShieldAlert, ShieldCheck, Target,
  Trophy, TrendingUp, Users, Layers, SquareTerminal, UserCheck, FlaskConical, Castle, Server, type LucideIcon,
} from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1] as const;

/** Pick an icon for a topic by keyword (English + Arabic). */
const ICON_RULES: [RegExp, LucideIcon][] = [
  // Most specific first. Use word boundaries for short tokens: a bare /ip/
  // matched "scripting" and "principles" and gave them a network icon.
  [/linux|windows|command line|\bshell\b|script|لينكس|ويندوز|سطر الأوامر/i, SquareTerminal],
  [/principle|\bcia\b|defen[cs]e in depth|least privilege|مبادئ|ثالوث/i, ShieldCheck],
  [/threat|malware|social engineering|تهديد|برمجيات خبيثة|هندسة اجتماعية/i, ShieldAlert],
  [/crypt|hash|تشفير|تجزئة/i, Lock],
  [/hygiene|attacker and a defender|نظافة/i, UserCheck],
  [/recon|osint|استطلاع/i, Radar],
  [/scan|enumerat|مسح|تعداد/i, ScanSearch],
  [/active directory|kerberos|\bdomain\b/i, Network],
  [/password|auth|credential|مصادقة|كلمات المرور/i, KeyRound],
  [/gaining|exploit|استغلال|الحصول على الوصول/i, Bug],
  [/maintain|privilege escalation|persist|escalat|الحفاظ|صلاحيات|ترسيخ/i, TrendingUp],
  [/clearing|forensic|tracks|إخفاء|أثر/i, EyeOff],
  [/report|methodolog|تقرير|منهجية/i, FileText],
  [/web|http|owasp|ويب/i, Globe],
  [/network|\bip\b|\bdns\b|ports|protocol|شبك/i, Network],
  [/computer|operating system|software|حاسوب|حواسيب|أنظمة التشغيل/i, Cpu],
  [/career|where to go next|مسار|الخطوة التالية/i, Compass],
  [/capstone|ختامي/i, Trophy],
];
export function topicIcon(text: string): LucideIcon {
  if (!text) return BookOpen;
  return ICON_RULES.find(([re]) => re.test(text))?.[1] ?? BookOpen;
}

export function LevelMeter({ level, color }: { level: number; color: string }) {
  return (
    <span className="inline-flex items-end gap-1" aria-hidden>
      {[1, 2, 3].map((n) => (
        <span key={n} className="w-1.5 rounded-sm" style={{ height: 6 + n * 5, background: n <= level ? color : "rgba(255,255,255,0.14)" }} />
      ))}
    </span>
  );
}

export const GLANCE_ICONS = { level: Target, lessons: BookOpen, labCount: FlaskConical, range: Network, domains: Castle, machines: Server, units: Layers, languages: Languages, labs: MonitorSmartphone };
export type GlanceIcon = keyof typeof GLANCE_ICONS;

/** Icons are passed by name: server pages cannot hand components to client ones. */
export function GlanceStrip({
  items,
}: {
  items: { label: string; value: ReactNode; icon: GlanceIcon }[];
}) {
  return (
    <ul className="grid grid-cols-2 gap-3 md:grid-cols-[repeat(var(--n),minmax(0,1fr))]" style={{ "--n": items.length } as CSSProperties}>
      {items.map(({ label, value, icon }, i) => {
        const Icon = GLANCE_ICONS[icon];
        return (
        <motion.li
          key={label}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
          className="fm-glass flex items-center gap-4 p-4"
        >
          <span className="fm-icon-tile shrink-0"><Icon className="size-6" /></span>
          <span className="min-w-0">
            <span className="block text-xs text-(--fm-text-secondary)">{label}</span>
            <span className="block font-heading text-base md:text-lg font-bold text-(--fm-text-primary) leading-tight">{value}</span>
          </span>
        </motion.li>
        );
      })}
    </ul>
  );
}

/** Numbered stage path drawn as a connected line of glowing nodes. */
export function StagePath({ stages, color }: { stages: string[]; color: string }) {
  return (
    <div className="fm-glass relative overflow-hidden p-6 md:p-10">
      <ol
        className="relative grid grid-cols-1 gap-6 md:gap-3 md:grid-cols-[repeat(var(--n),minmax(0,1fr))]"
        style={{ "--n": stages.length } as CSSProperties}
      >
        <motion.span
          aria-hidden
          className="absolute hidden md:block top-7 h-px origin-left rtl:origin-right"
          style={{ insetInlineStart: `${50 / stages.length}%`, insetInlineEnd: `${50 / stages.length}%`, background: `linear-gradient(90deg, ${color}, #E8C84A)` }}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        {stages.map((s, i) => {
          const Icon = topicIcon(s);
          return (
            <motion.li
              key={s}
              initial={{ opacity: 0, y: 14, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.12, ease: EASE }}
              className="relative flex flex-col items-center text-center gap-3"
            >
              <span
                className="relative grid place-items-center size-14 rounded-2xl border bg-(--fm-background)"
                style={{ borderColor: `${color}66`, boxShadow: `0 0 24px -6px ${color}` }}
              >
                <Icon className="size-6" style={{ color }} />
                <span className="absolute -top-2 -end-2 grid place-items-center size-6 rounded-full bg-(--fm-gold) text-(--fm-navy) text-xs font-bold">
                  {i + 1}
                </span>
              </span>
              <span className="text-sm font-semibold text-(--fm-text-primary) leading-snug">{s}</span>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
}

/** "What you'll learn" as icon tiles; titles first, detail small. */
export function TopicGrid({ items, color }: { items: { title: string; detail: string }[]; color: string }) {
  return (
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((it, i) => {
        const byTitle = topicIcon(it.title);
        const Icon = byTitle === BookOpen ? topicIcon(it.detail) : byTitle;
        return (
          <motion.li
            key={it.title + i}
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.05, ease: EASE }}
            className="fm-glass fm-glass-hover flex gap-4 p-5"
          >
            <span
              className="grid place-items-center size-11 shrink-0 rounded-xl border"
              style={{ borderColor: `${color}55`, background: `radial-gradient(circle at 30% 25%, ${color}30, rgba(10,22,40,0.8) 70%)` }}
            >
              <Icon className="size-5" style={{ color }} />
            </span>
            <span className="min-w-0 flex flex-col">
              <span className="block font-semibold text-(--fm-text-primary) leading-snug">{it.title}</span>
              {it.detail && <span className="mt-1 text-sm text-(--fm-text-secondary) line-clamp-2">{it.detail}</span>}
            </span>
          </motion.li>
        );
      })}
    </ul>
  );
}

export const FACT_ICONS = { who: Users, prerequisites: ListChecks, outcome: Trophy };

export function FactCard({ title, icon, children, i }: { title: string; icon: keyof typeof FACT_ICONS; children: ReactNode; i: number }) {
  const Icon = FACT_ICONS[icon];
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
      className="fm-glass p-6"
    >
      <span className="fm-icon-tile mb-4"><Icon className="size-6" /></span>
      <h3 className="font-heading text-lg font-bold text-(--fm-text-primary) mb-2">{title}</h3>
      <div className="text-sm text-(--fm-text-secondary) leading-relaxed [&_strong]:text-(--fm-text-primary)">{children}</div>
    </motion.div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="font-heading text-2xl md:text-3xl font-bold text-(--fm-text-primary) mb-6">{children}</h2>;
}
